import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import Coupon from '@alltrails/core/types/Coupon';
import { getPriceTotal } from '@alltrails/payment/utils/plan';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type SubscriptionUser from '@alltrails/types/SubscriptionUser';
import getFreeTrialDurationPricing from './getFreeTrialDurationPricing';

interface Params {
  currencyCode: CurrencyCode;
  plan: Plan;
  isMobileSizedScreen: boolean;
  isPromoEligible?: boolean;
  coupon?: Coupon;
  subscriptionUser: SubscriptionUser | null;
  // Drops the monthly breakdown from the trial copy entirely ("7 days free, then $X/year"). Used by the single-tier plans-page subheading.
  compactTrial?: boolean;
  // Keeps the monthly breakdown in the trial copy but drops the "that's" prefix on desktop ("... ($Y/month)"). Used by the tier-selector cards.
  compactMonthly?: boolean;
  // Renders the annual amount in Medium weight. Used by the plans-page subheadings and tier-selector cards.
  emphasizeAnnualCost?: boolean;
}

const getPricingText = ({
  plan,
  currencyCode,
  isPromoEligible,
  coupon,
  isMobileSizedScreen,
  subscriptionUser,
  compactTrial,
  compactMonthly,
  emphasizeAnnualCost
}: Params) => {
  const rawAnnualCost = <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />;
  // Match the weight to the surrounding text size: tier-selector cards render at text150 (14px), the plans subheading at text200 (16px).
  const emphasizedVariant = compactMonthly ? 'text150bold' : 'text200bold';
  const annualCost = emphasizeAnnualCost ? (
    <Typography component="span" variant={emphasizedVariant}>
      {rawAnnualCost}
    </Typography>
  ) : (
    rawAnnualCost
  );
  const monthlyCost = <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />;

  if (coupon?.type === 'FreeTrialCoupon') {
    return getFreeTrialDurationPricing(plan, currencyCode, coupon);
  }

  if (coupon?.type === 'SubscriptionCoupon') {
    return (
      <FormattedMessage
        defaultMessage="{promoAnnualCost} for 1 year, then {annualCost}/year"
        values={{
          promoAnnualCost: <FormattedNumber currency={currencyCode} style="currency" value={getPriceTotal(plan, coupon)} />,
          annualCost
        }}
      />
    );
  }

  if (isPromoEligible) {
    if (compactTrial) {
      return (
        <FormattedMessage
          defaultMessage="7 days free, then <b>{annualCost}</b>/year"
          values={{
            annualCost: rawAnnualCost,
            b: (chunks: ReactNode) => (
              <Typography component="span" variant={emphasizedVariant}>
                {chunks}
              </Typography>
            )
          }}
        />
      );
    }

    if (isMobileSizedScreen || compactMonthly) {
      return <FormattedMessage defaultMessage="7 days free, then {annualCost}/year ({monthlyCost}/month)" values={{ annualCost, monthlyCost }} />;
    }

    return (
      <FormattedMessage defaultMessage="7 days free, then {annualCost}/year (that’s {monthlyCost}/month)" values={{ annualCost, monthlyCost }} />
    );
  }

  // feels little backwards
  if (subscriptionUser?.canUpgradeExistingSubscription && !subscriptionUser?.subscription?.trial) {
    return (
      <FormattedMessage
        defaultMessage="{annualCost}/year, and we’ll <b>refund</b> the rest of your current plan"
        values={{
          annualCost: (
            <b>
              <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />
            </b>
          ),
          b: chunks => <b>{chunks}</b>
        }}
      />
    );
  }

  return <FormattedMessage defaultMessage="{annualCost}/year (that’s {monthlyCost}/month)" values={{ annualCost, monthlyCost }} />;
};

export default getPricingText;
